import React, { useMemo } from 'react';
import type { DataTableRenderCellParams, DataTableRowEventHandler } from '@/components/custom/erp/data-table-v2';
import type { ButtonType } from '@/components/custom/erp/input-table-v2';
import { EditableTableWithContext } from '../EditableTableWithContext';
import type { RepeatRowsPayload } from '@/components/custom/erp';
import { SelectedCellInfo } from '../hooks/useDetailRows';
import { getFeeTableColumns } from './columns';
import { FormMode } from '@/types/form';

interface FeeTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: DataTableRowEventHandler<MuaHangRecord>;
  onCellClick?: (params: DataTableRenderCellParams<MuaHangRecord>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onRepeatForRows: (payload: RepeatRowsPayload<MuaHangRecord>) => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
}

const FEE_ACTION_ORDER: ButtonType[] = ['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'export', 'pin'];
const noop = () => {};

export const FeeTab: React.FC<FeeTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onRepeatForRows,
  onCellValueChange
}) => {
  const columns = useMemo(() => getFeeTableColumns(onCellValueChange), [onCellValueChange]);
  const tableActions = useMemo(
    () => ({
      add: onAddRow,
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down'),
      export: noop,
      pin: noop
    }),
    [onAddRow, onCopyRow, onDeleteRow, onMoveRow, onPasteRow]
  );

  return (
    <EditableTableWithContext
      formMode={formMode}
      rows={rows}
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      selectedCell={selectedCell}
      columns={columns}
      onRepeatForRows={onRepeatForRows}
      actions={tableActions}
      actionOrder={FEE_ACTION_ORDER}
    />
  );
};

export default FeeTab;
